/* Sea-area currents — the wire format and the maths, kept pure.
 *
 * Plain `.ts`, not `.svelte.ts`, so `mock:verify` can import it under bare
 * node: the harness has to be able to assert the unpack and the interpolation
 * without a Svelte preprocessor. The reactive store adds state on top
 * (seacurrents.svelte.ts) and nothing else.
 *
 * ═══ WHAT THIS LAYER IS ALLOWED TO CLAIM ═══
 *
 * `docs/investigations/currents-map.md` §3b rejects an interpolated field
 * between the CHS stations, and that rejection STANDS: across-channel reversal
 * at Dodd and Active Pass means a smooth field between two opposite shores is
 * a statement the data never made.
 *
 * What makes this honest is that it is not that. SalishSeaCast is itself a
 * gridded NEMO solution at ~500 m — the interpolation happened inside the
 * model, with the physics — and the pi hands us a decimation of it. Two rules
 * follow, and both are enforced by the layer rather than by good intentions:
 * the stations always draw ON TOP, because they are finer than this in the
 * passes; and every place a user can see it, this says "model, not measured".
 */

import type { FieldSampler } from './windparticles.ts';

/** The lattice the pi's `/signalk/marine-data/seacurrents` route returns.
 *  `u`/`v` are metres per second, east and north, row-major with row 0 on the
 *  NORTH edge — deliberately the same convention as WindFieldGrid, so the
 *  particle renderer reads both fields with one implementation. `null` means
 *  the model has no water there. */
export interface SeaCurrentLattice {
  west: number;
  south: number;
  east: number;
  north: number;
  /** Kept for any consumer that only knows square lattices. */
  step: number;
  /** THE TRUTHFUL PAIR. 500 m is 0.0045° of latitude but 0.0068° of longitude
   *  at 49°N, so a lattice that is square in DEGREES is a lattice that throws
   *  away half its north-south detail — or holes east-west. The pi resamples
   *  onto one that is square ON THE GROUND, and these are its steps. */
  stepX?: number;
  stepY?: number;
  cols: number;
  rows: number;
  u: (number | null)[];
  v: (number | null)[];
}

export interface SeaCurrentHour {
  /** The model hour this is, epoch ms. */
  t: number;
  lattice: SeaCurrentLattice;
}

/** What the pi sends. */
export interface SeaCurrentResponse {
  at: number;
  time: string;
  source: string;
  license: string;
  note: string;
  lattice: SeaCurrentLattice;
}

export const MS_TO_KN = 1.94384;

/** A speed/direction grid in exactly the shape the particle renderer wants. */
export interface CurrentGrid {
  west: number;
  south: number;
  east: number;
  north: number;
  step: number;
  cols: number;
  rows: number;
  stepX?: number;
  stepY?: number;
  /** KNOTS, row-major, row 0 = the NORTH edge. 0 where there is no model water. */
  speed: Float32Array;
  /** Degrees true the water is flowing FROM, so the renderer's FROM→TO
   *  convention drifts a particle the way the current SETS.
   *
   *  A current is quoted by its SET — where it goes — and wind by where it
   *  comes from. The renderer only knows one convention, so the conversion
   *  happens here, once, where it can be tested, rather than by remembering to
   *  add 180 at four call sites. */
  dir: Float32Array;
}

function toGrid(l: SeaCurrentLattice, speed: Float32Array, dir: Float32Array): CurrentGrid {
  return {
    west: l.west,
    south: l.south,
    east: l.east,
    north: l.north,
    step: l.step,
    stepX: l.stepX,
    stepY: l.stepY,
    cols: l.cols,
    rows: l.rows,
    speed,
    dir
  };
}

/** u/v (m/s, east/north) → knots and the FROM bearing the renderer expects. */
export function uvToSpeedDir(u: number, v: number): { kn: number; dirFrom: number } {
  const kn = Math.hypot(u, v) * MS_TO_KN;
  // atan2(east, north) is the compass bearing the water is SETTING toward.
  const setTo = ((Math.atan2(u, v) * 180) / Math.PI + 360) % 360;
  return { kn, dirFrom: (setTo + 180) % 360 };
}

/** One model hour as a renderable grid. */
export function gridFor(l: SeaCurrentLattice): CurrentGrid {
  const n = l.cols * l.rows;
  const speed = new Float32Array(n);
  const dir = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const u = l.u[i];
    const v = l.v[i];
    if (u === null || v === null || u === undefined || v === undefined) continue;
    const s = uvToSpeedDir(u, v);
    speed[i] = s.kn;
    dir[i] = s.dirFrom;
  }
  return toGrid(l, speed, dir);
}

/**
 * The field at an arbitrary instant, blended between the two model hours
 * either side of it.
 *
 * The model publishes on the hour and the app's time cursor does not, so
 * snapping to the nearest hour would make a scrub jump in visible steps and,
 * worse, would show a flood as a slack for up to half an hour either side of
 * the turn. Blending u and v as VECTORS — not speed and direction separately —
 * is what makes a reversing stream pass through slack instead of spinning
 * through 180° of bearing at full rate.
 */
export function blendHours(
  a: SeaCurrentHour,
  b: SeaCurrentHour | null,
  t: number
): CurrentGrid | null {
  if (!b || b.t === a.t) return gridFor(a.lattice);
  const la = a.lattice;
  const lb = b.lattice;
  // A lattice mismatch means the pi changed its box between hours; the older
  // one alone is honest, a mixture of two different grids is not.
  if (la.cols !== lb.cols || la.rows !== lb.rows || la.west !== lb.west) {
    return gridFor(la);
  }
  const w = Math.max(0, Math.min(1, (t - a.t) / (b.t - a.t)));
  const n = la.cols * la.rows;
  const speed = new Float32Array(n);
  const dir = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const ua = la.u[i];
    const va = la.v[i];
    const ub = lb.u[i];
    const vb = lb.v[i];
    if (ua === null || va === null || ua === undefined || va === undefined) continue;
    // One side dry: keep the wet side rather than fading into a phantom.
    const u = ub === null || ub === undefined ? ua : ua + (ub - ua) * w;
    const v = vb === null || vb === undefined ? va : va + (vb - va) * w;
    const s = uvToSpeedDir(u, v);
    speed[i] = s.kn;
    dir[i] = s.dirFrom;
  }
  return toGrid(la, speed, dir);
}

/** The model hour containing `t` — the pi keys its cache the same way. */
export function modelHour(t: number): number {
  return Math.floor(t / 3_600_000) * 3_600_000;
}

/** The two hours a blend at `t` needs. */
export function hoursFor(t: number): [number, number] {
  const h = modelHour(t);
  return [h, h + 3_600_000];
}

/* ═══════════════════════════════════════════════════════════════════════════
 * THE PACKED WINDOW — many hours, held at once, sampled per frame
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Everything above is the ONE-HOUR JSON path, which is still the mock's and the
 * fallback's. It cannot be the scrubbing path, for three measured reasons:
 *
 *   - an hour is 3.5 MB of JSON, so the store could only afford to hold two,
 *     and a drag across the scrubber's 60 h crosses ~60 hour boundaries;
 *   - `JSON.parse` of one is 11 ms and `gridFor` another 5;
 *   - `blendHours` allocates 335 646 cells twice on every cursor move, and the
 *     renderer reads ~950 of them.
 *
 * So the pi also serves the whole window as one binary blob, and this is its
 * shape in memory. Two ideas do all the work:
 *
 *   WET CELLS ONLY. 55 484 of the lattice's 335 646 cells carry water. The mask
 *   is the coastline, so it is the same every hour and costs one Int32 index
 *   for the entire window — and each hour is then 55 484 pairs instead of
 *   335 646.
 *
 *   CENTIMETRES PER SECOND, Int16. The model's own maximum is 2.585 m/s; the
 *   type reaches 327 m/s. A model current is not precise to a millimetre a
 *   second, so the rounding is below the noise, and an hour costs 222 kB
 *   instead of 3.5 MB. Forty-two hours is about 9.3 MB — a window a tablet can
 *   simply hold, which is what makes a scrub cost no network at all.
 */

/** The lattice geometry plus the wet-cell mask, shared by every hour. */
export interface CurrentGeom {
  west: number;
  south: number;
  east: number;
  north: number;
  step: number;
  stepX: number;
  stepY: number;
  cols: number;
  rows: number;
  /** Cell index (row-major, row 0 = NORTH) → slot in the wet arrays, or -1 for
   *  dry. 1.3 MB, held once for the whole window — the price of random access
   *  into a wet-only payload, and the only way the sampler can be O(1). */
  lookup: Int32Array;
  /** How many wet slots each hour carries. */
  wet: number;
}

/** One model hour, wet cells only, centimetres per second. */
export interface PackedHour {
  t: number;
  u: Int16Array;
  v: Int16Array;
}

export interface CurrentWindow {
  geom: CurrentGeom;
  hours: PackedHour[];
  at: number;
  source: string;
  license: string;
  note: string;
  /** Where the MODEL starts and stops, epoch ms on the hour — not where this
   *  reply starts and stops.
   *
   *  SalishSeaCast publishes a rolling window, roughly 135 h back and only 30 h
   *  forward, while the scrubber reaches +48 h. Without this the client asked
   *  for hours that cannot exist, ERDDAP 404'd the whole request, the pi
   *  reported 502, and the store treated a perfectly ordinary "past the
   *  forecast" as an outage — which took the layer off the map. Optional, so a
   *  pi that predates it still decodes. */
  coverage: { from: number; to: number } | null;
}

const CM_TO_MS = 0.01;

/** The provenance fields, without any hours — the JSON route carries them on
 *  the response rather than in the payload. */
function emptyWindow(geom: CurrentGeom): CurrentWindow {
  return {
    geom,
    hours: [],
    at: Date.now(),
    source: 'SalishSeaCast NEMO (UBC)',
    license: 'Apache-2.0',
    note: '',
    coverage: null
  };
}

/**
 * Turn one JSON lattice into the packed form, so the fallback path and the
 * window path meet at the same sampler rather than at two of them.
 *
 * `into` packs against an EXISTING mask instead of building a new one, and it
 * is what keeps the single-hour path safe. Every hour is indexed through the
 * shared wet mask, so two hours packed with two different masks cannot be
 * blended — and a store that drops the mismatched one would then re-fetch it,
 * drop the other, and loop on multi-megabyte requests for ever. The mask is
 * the coastline and does not change, so reusing the first one is both correct
 * and the only way that loop is impossible rather than merely unlikely.
 */
export function packLattice(
  t: number,
  l: SeaCurrentLattice,
  into?: CurrentGeom
): CurrentWindow {
  const n = l.cols * l.rows;
  const reuse = into && into.cols === l.cols && into.rows === l.rows && into.west === l.west;
  const lookup = reuse ? into!.lookup : new Int32Array(n).fill(-1);
  let wet = reuse ? into!.wet : 0;
  if (!reuse) {
    for (let i = 0; i < n; i++) {
      const u = l.u[i];
      const v = l.v[i];
      if (u === null || v === null || u === undefined || v === undefined) continue;
      lookup[i] = wet++;
    }
  }
  const u = new Int16Array(wet);
  const v = new Int16Array(wet);
  for (let i = 0; i < n; i++) {
    const slot = lookup[i]!;
    if (slot < 0) continue;
    const a = l.u[i];
    const b = l.v[i];
    // Wet in the mask but dry this hour: slack is the honest zero.
    if (a === null || b === null || a === undefined || b === undefined) continue;
    u[slot] = Math.round(a * 100);
    v[slot] = Math.round(b * 100);
  }
  if (reuse) return { ...emptyWindow(into!), hours: [{ t, u, v }] };
  return {
    geom: {
      west: l.west,
      south: l.south,
      east: l.east,
      north: l.north,
      step: l.step,
      stepX: l.stepX ?? l.step,
      stepY: l.stepY ?? l.step,
      cols: l.cols,
      rows: l.rows,
      lookup,
      wet
    },
    hours: [{ t, u, v }],
    at: Date.now(),
    source: 'SalishSeaCast NEMO (UBC)',
    license: 'Apache-2.0',
    note: '',
    coverage: null
  };
}


/** The window's binary wire format, matching `ops/chart-cache/lib/seacurrents.js`.
 *
 *  `SCW1` | Int32 header length | header JSON (padded to 4) | Int32 wet index |
 *  per hour: Int16 u[wet], Int16 v[wet].
 *
 *  A hand-rolled format rather than JSON because the whole point is that the
 *  payload is 16× smaller; and length-prefixed rather than self-describing
 *  because both ends live in this repo and a version tag is cheaper than a
 *  schema. `SCW1` is checked, so a proxy that hands back an HTML error page
 *  fails loudly instead of decoding into noise. */
export function decodeWindow(buf: ArrayBuffer): CurrentWindow {
  const dv = new DataView(buf);
  if (buf.byteLength < 8) throw new Error('window too short');
  if (dv.getUint8(0) !== 0x53 || dv.getUint8(1) !== 0x43 || dv.getUint8(2) !== 0x57) {
    throw new Error('not a current window');
  }
  const headLen = dv.getInt32(4, false);
  if (headLen <= 0 || 8 + headLen > buf.byteLength) throw new Error('bad window header');
  const head = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, headLen))) as {
    west: number;
    south: number;
    east: number;
    north: number;
    step: number;
    stepX: number;
    stepY: number;
    cols: number;
    rows: number;
    wet: number;
    hours: number[];
    at: number;
    source: string;
    license: string;
    note: string;
    coverage?: { from: number; to: number } | null;
  };

  /* `headLen` counts the JSON only. The writer pads after it so the Int32 wet
   * index lands on a 4-byte boundary, because a typed-array view onto a
   * misaligned offset throws in every browser. */
  let off = 8 + headLen;
  off += (4 - (off % 4)) % 4;
  if (off + head.wet * 4 > buf.byteLength) throw new Error('window is truncated');
  const wetIndex = new Int32Array(buf, off, head.wet);
  off += head.wet * 4;

  const lookup = new Int32Array(head.cols * head.rows).fill(-1);
  for (let s = 0; s < head.wet; s++) lookup[wetIndex[s]!] = s;

  const hours: PackedHour[] = [];
  for (const t of head.hours) {
    /* COPIED, not viewed. An Int16Array view onto the response buffer would
     * pin the whole multi-megabyte blob in memory for as long as any one hour
     * of it is held — and the store holds hours individually so it can drop
     * the ones the cursor has left behind. */
    const u = new Int16Array(head.wet);
    const v = new Int16Array(head.wet);
    u.set(new Int16Array(buf, off, head.wet));
    off += head.wet * 2;
    v.set(new Int16Array(buf, off, head.wet));
    off += head.wet * 2;
    hours.push({ t, u, v });
  }

  return {
    geom: {
      west: head.west,
      south: head.south,
      east: head.east,
      north: head.north,
      step: head.step,
      stepX: head.stepX,
      stepY: head.stepY,
      cols: head.cols,
      rows: head.rows,
      lookup,
      wet: head.wet
    },
    hours,
    at: head.at,
    source: head.source,
    license: head.license,
    note: head.note,
    coverage: head.coverage ?? null
  };
}

/**
 * The field at an instant, as a function of position.
 *
 * ═══ WHY THIS IS NOT `blendHours` FOLLOWED BY `sampleField` ═══
 *
 * It is exactly that, arithmetically — and deliberately so, because that pair
 * is what the tests and the mock harness pin. What changes is the ORDER of the
 * two loops: instead of blending 335 646 cells and then reading 950 of them,
 * this blends the four cells around each of the 950 points. Same answer, ~350×
 * less work, and — the reason it matters — cheap enough to run per FRAME, so
 * dragging the scrubber moves the field continuously instead of in one-second
 * steps.
 *
 * The two rules that must survive the reordering:
 *
 *   TIME IS A VECTOR LERP of u and v, per cell, BEFORE anything becomes a speed
 *   or a bearing. That is what makes a reversing stream pass through slack
 *   rather than swinging 180° at full rate.
 *
 *   SPACE averages the unit DIRECTION and the speed MAGNITUDE separately, the
 *   way `sampleField` does — not the raw vectors. Two neighbouring cells 180°
 *   apart across a channel must not average to a phantom slack; they average to
 *   a direction with the speed intact, which is at least honest about the
 *   speed. Matching `sampleField` here is also what lets the wind field and the
 *   current field share one renderer.
 */
/** Where between two held hours `t` falls, 0..1. */
export function hourBlend(a: PackedHour, b: PackedHour | null, t: number): number {
  return !b || b.t === a.t ? 0 : Math.max(0, Math.min(1, (t - a.t) / (b.t - a.t)));
}

/**
 * One sample of the blended field, as {spd, dirFrom}.
 *
 * Pulled out of `windowSampler` so the ROUTER can share it. The router needs a
 * sampler that takes `t` per call — it walks a passage forward in time, where
 * the particle renderer holds one instant and asks about many places — and a
 * second copy of this interpolation is the last thing this codebase needs. Two
 * implementations of "what is the water doing here" is how the route and the
 * layer drawn under it come to disagree.
 */
export function sampleBlend(
  geom: CurrentGeom,
  a: PackedHour,
  b: PackedHour | null,
  w: number,
  lat: number,
  lon: number
): { spd: number; dirFrom: number } | null {
  const ua = a.u;
  const va = a.v;
  const ub = b && b.t !== a.t ? b.u : null;
  const vb = b && b.t !== a.t ? b.v : null;
  const { cols, rows, west, north, stepX, stepY, lookup } = geom;
  {
    if (lat < geom.south || lat > geom.north || lon < geom.west || lon > geom.east) return null;
    // Row 0 is the NORTH edge — the same convention as WindFieldGrid.
    const fy = (north - lat) / stepY;
    const fx = (lon - west) / stepX;
    const y0 = Math.floor(fy);
    const x0 = Math.floor(fx);
    const ty = fy - y0;
    const tx = fx - x0;

    let wsum = 0;
    let su = 0;
    let sv = 0;
    let spd = 0;
    for (let dy = 0; dy <= 1; dy++) {
      const y = y0 + dy;
      if (y < 0 || y >= rows) continue;
      const wy = dy ? ty : 1 - ty;
      if (wy <= 0) continue;
      for (let dx = 0; dx <= 1; dx++) {
        const x = x0 + dx;
        if (x < 0 || x >= cols) continue;
        const wx = dx ? tx : 1 - tx;
        if (wx <= 0) continue;
        const slot = lookup[y * cols + x]!;
        if (slot < 0) continue; // dry: the model has no water here
        const u0 = ua[slot]! * CM_TO_MS;
        const v0 = va[slot]! * CM_TO_MS;
        const u = ub ? u0 + (ub[slot]! * CM_TO_MS - u0) * w : u0;
        const v = vb ? v0 + (vb[slot]! * CM_TO_MS - v0) * w : v0;
        const mag = Math.hypot(u, v);
        if (mag <= 0) continue; // slack to the model's own precision
        const wgt = wx * wy;
        /* The FROM unit vector, without a round trip through degrees: the water
         * sets toward (u, v), so it comes from (-u, -v). */
        su -= (wgt * u) / mag;
        sv -= (wgt * v) / mag;
        spd += wgt * mag * MS_TO_KN;
        wsum += wgt;
      }
    }
    if (wsum <= 0) return null;
    const dirFrom = ((Math.atan2(su / wsum, sv / wsum) * 180) / Math.PI + 360) % 360;
    return { spd: spd / wsum, dirFrom };
  }
}

/** The particle renderer's view: one instant, many places. */
export function windowSampler(
  geom: CurrentGeom,
  a: PackedHour,
  b: PackedHour | null,
  t: number
): FieldSampler {
  /* Computed ONCE — a per-particle divide is 950 of them a frame for a number
   * that cannot change inside a frame. */
  const w = hourBlend(a, b, t);
  return (lat: number, lon: number) => sampleBlend(geom, a, b, w, lat, lon);
}

/** The two hours a sampler at `t` needs, out of what is held. Null when nothing
 *  brackets `t` — which is how "past the model's reach" is expressed. */
export function bracket(
  hours: Map<number, PackedHour>,
  t: number
): [PackedHour, PackedHour | null] | null {
  const [h0, h1] = hoursFor(t);
  const a = hours.get(h0);
  if (!a) return null;
  return [a, hours.get(h1) ?? null];
}

/**
 * How the layer describes itself.
 *
 * A getter on the module rather than a string in the panel, because the honest
 * framing changes with the data: an age, a model name, and — when the CHS
 * stations disagree with it, which they will in the passes — which one wins.
 */
export function provenance(o: {
  source: string;
  ageMin: number | null;
  stale: boolean;
}): string {
  const age =
    o.ageMin === null
      ? 'not loaded'
      : o.ageMin < 90
        ? `${Math.max(0, Math.round(o.ageMin))} min old`
        : `${(o.ageMin / 60).toFixed(1)} h old`;
  return (
    `${o.source} — a MODEL, not a measurement: currents averaged in ~500 m squares. ` +
    `${o.stale ? 'STALE: ' : ''}${age}. ` +
    'The CHS current stations are the authority where they exist and always draw on top.'
  );
}

/**
 * The ROUTER's view: one place, many times.
 *
 * The particle renderer holds an instant and asks about a thousand positions;
 * a passage walks forward in time and asks about the water ahead of it. Same
 * field, opposite axis — so this takes `t` per call and shares `sampleBlend`
 * with `windowSampler` rather than reimplementing the interpolation.
 *
 * ═══ NULL PAST THE MODEL'S REACH, WHICH IS THE HONEST DEGRADE ═══
 *
 * SalishSeaCast publishes a rolling window of a day or two while the CHS
 * station predictions cover the year. A departure next week therefore has
 * gates and no field, and `bracket` returning null is how that is said: the
 * passage falls back to boat speed alone and the schedule reports
 * `currentsUsed: false`, so a panel can tell the user the route was timed on
 * the narrows and not on the water. Returning zero here instead would claim
 * slack water across the whole Strait of Georgia.
 *
 * `setToDeg`, not `dirFrom`: a passage cares where the water is PUSHING it,
 * and the renderer cares where the arrows point from. Converted once, here,
 * rather than at every call site that could get it backwards.
 */
export function windowCurrentSampler(
  geom: CurrentGeom,
  hours: Map<number, PackedHour>
): (lat: number, lon: number, t: number) => { setToDeg: number; kn: number } | null {
  return (lat, lon, t) => {
    const pair = bracket(hours, t);
    if (!pair) return null;
    const [a, b] = pair;
    const got = sampleBlend(geom, a, b, hourBlend(a, b, t), lat, lon);
    if (!got) return null;
    return { setToDeg: (got.dirFrom + 180) % 360, kn: got.spd };
  };
}
